import { Plus } from "lucide-react";
import { useState } from "react";
import { Button } from "../../../components/button";
import PageHeader from "../../../components/common/PageHeader";
import { DialogV2 } from "../../../components/dialog-v2";
import { Input } from "../../../components/input";
import { MerchantCategoryEditor } from "../components/MerchantCategoryEditor";
import { MerchantCategoriesTable } from "../components/MerchantCategoriesTable";
import { useMerchantCategories } from "../hooks/useMerchantCategories";
import type { MerchantCategory, MerchantCategoryPayload } from "../types/merchantCategory.types";

export function MerchantCategoriesPage() {
  const catalog = useMerchantCategories();
  const [editing, setEditing] = useState<MerchantCategory | null>(null);
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const openCreate = () => {
    setEditing(null);
    setActionError(null);
    setDialogOpen(true);
  };

  const openEdit = (category: MerchantCategory) => {
    setEditing(category);
    setActionError(null);
    setDialogOpen(true);
  };

  const submit = async (payload: MerchantCategoryPayload) => {
    try {
      await catalog.save(editing?.id, payload);
      setDialogOpen(false);
    } catch (requestError) {
      setActionError(
        requestError instanceof Error ? requestError.message : "Unable to save merchant category",
      );
    }
  };

  const remove = async (category: MerchantCategory) => {
    if (!window.confirm(`Delete ${category.description}? This cannot be undone.`)) return;
    try {
      await catalog.remove(category.id);
    } catch (requestError) {
      setActionError(
        requestError instanceof Error ? requestError.message : "Unable to delete merchant category",
      );
    }
  };

  return (
    <div className="min-h-screen bg-background p-4 md:p-6">
      <PageHeader
        title="Merchant Categories"
        subtitle="Manage merchant category records."
        actions={
          <Button onClick={openCreate}>
            <Plus className="mr-2 h-4 w-4" />
            Add Merchant Category
          </Button>
        }
      />
      <div className="rounded-xl border border-[#DCDCDC] bg-white p-4 shadow-sm">
        <div className="mb-4 flex flex-wrap gap-3">
          <Input
            value={catalog.keyword}
            onChange={(event) => {
              catalog.setKeyword(event.target.value);
              catalog.setPage(1);
            }}
            placeholder="Search merchant categories"
            className="max-w-md"
          />
          <Button
            variant="outline"
            onClick={() => void catalog.refetch()}
          >
            Refresh
          </Button>
        </div>
        {actionError ? <p className="mb-3 text-sm text-[#EC5454]">{actionError}</p> : null}
        <MerchantCategoriesTable
          categories={catalog.items}
          loading={catalog.isLoading}
          error={catalog.error}
          onRetry={() => void catalog.refetch()}
          pagination={{
            page: catalog.page,
            totalPages: Math.max(1, Math.ceil(catalog.total / 20)),
            onPageChange: catalog.setPage,
          }}
          onEdit={openEdit}
          onDelete={(category) => void remove(category)}
          onReorder={async (orderedIds) => {
            try {
              await catalog.reorder(orderedIds);
            } catch (requestError) {
              setActionError(
                requestError instanceof Error
                  ? requestError.message
                  : "Unable to reorder merchant categories",
              );
              await catalog.refetch();
            }
          }}
        />
      </div>
      <DialogV2
        open={isDialogOpen}
        onClose={() => setDialogOpen(false)}
        title={`${editing ? "Edit" : "Add"} Merchant Category`}
        maxWidth="md"
        actions={
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              form="merchant-category-form"
              type="submit"
            >
              Save
            </Button>
          </div>
        }
      >
        <MerchantCategoryEditor
          formId="merchant-category-form"
          category={editing}
          initialParentOption={
            editing?.parent_merchant_category_id
              ? catalog.items.find((item) => item.id === editing.parent_merchant_category_id)
              : undefined
          }
          isOpen={isDialogOpen}
          onSubmit={submit}
        />
      </DialogV2>
    </div>
  );
}
